/**
 * Flow registry.
 *
 * A "flow" is one logical operation a screen performs — `withdraw`,
 * `openDispute`, `addCollateral`. Each may register an on-chain verifier per
 * step, which is what lets recovery answer "did this already happen?" when we
 * never saw a transaction hash.
 *
 * That verifier is the single most valuable thing a flow can provide. It is one
 * `eth_call`, it works for ERC-4337 smart accounts (whose nonce never moves),
 * and it is exactly the check that would have let the stranded withdraw resume
 * on its own — the router already exposes `getWithdrawAmount(user).depositLocked`.
 */

const flows = new Map();

/**
 * @param {object} flow
 * @param {string} flow.id
 * @param {Record<string, {verifyOnChain?: Function, effect?: Function}>} flow.steps
 */
export function registerFlow(flow) {
  if (!flow || !flow.id) throw new Error("flow needs an id");
  flows.set(flow.id, flow);
  return flow;
}

export function getFlow(flowId) {
  return flows.get(flowId) || null;
}

/**
 * Step descriptor as `recovery.resolveEntry` wants it: an object that may carry
 * `verifyOnChain`. Returns null when the flow or step is unregistered, which is
 * fine — recovery just falls back to hash/nonce resolution.
 */
export function getStep(flowId, stepId) {
  const flow = flows.get(flowId);
  if (!flow || !flow.steps) return null;
  return flow.steps[stepId] || null;
}

export const __testing = {
  reset: () => flows.clear(),
  flows,
};
